// ContactEditHeaderBar — the left-column header chrome extracted verbatim from
// ContactEditModal.tsx's modalContent JSX: the fixed header bar (back / name /
// AI / Edit-Done / mobile stage dropdown / overflow-menu trigger), the
// scrollable identity block (avatar, name, stage/engagement/relationship/
// auto-enrich badges), and the mobile action bar (AI / Edit / Save / stage).
//
// The scrollable content body (freeform / view / edit ternary) is passed in as
// `scrollBody` and rendered inside the same `overflowY:auto` div with the
// orchestrator's markDirty onInput/onChange — so the scroll container and its
// children stay byte-identical to the inline original.
//
// Pure presentational leaf: no state/effects of its own. The Edit/Done toggle
// onClick bodies (desktop + mobile) are lifted verbatim — they read the field
// values + setters passed as props, exactly as the inline handlers did. No
// `fetch` here.
import React from 'react';
import { formatPersonName } from '@/lib/formatPersonName';
import type { CRMColumnConfig } from '@/types/modules';
import { activitiesToNotes } from '../lib/contactMapping';
import type { Activity, ContactObject } from '../lib/types';
import { HeaderOverflowMenuItems } from './HeaderOverflowMenuItems';
import { ArchiveContactConfirmationModal } from './ArchiveContactConfirmationModal';
import { ContactProvenanceBadges } from '../../components/ContactProvenanceBadges';
import { ContactConflictBadges } from '../../components/ContactConflictBadges';

export interface ContactEditHeaderBarProps {
  isMobile: boolean;
  viewOnly: boolean;
  isNew: boolean | undefined;
  isSaving: boolean | undefined;
  freeformMode: boolean | undefined;
  isViewMode: boolean;
  setIsViewMode: (b: boolean) => void;
  onClose: () => void;
  contact: ContactObject | null | undefined;
  columns: CRMColumnConfig[];
  stage: string;
  showStageDropdown: boolean;
  setShowStageDropdown: (b: boolean) => void;
  stageDropdownRef: React.RefObject<HTMLDivElement | null>;
  handleStageChange: (newStage: string) => void;
  showHeaderMenu: boolean;
  setShowHeaderMenu: (b: boolean) => void;
  headerMenuRef: React.RefObject<HTMLDivElement | null>;
  onArchive?: (id: string) => void;
  onDelete?: (id: string) => void | Promise<void>;
  showArchiveConfirm: boolean;
  setShowArchiveConfirm: (b: boolean) => void;
  setDeleteConfirmation: (v: { contactId: string; name: string } | null) => void;
  enrichLoading: boolean;
  handleEnrich: (extraHintsArg?: string | unknown) => void;
  // identity fields
  name: string;
  firstName: string;
  lastName: string;
  displayNameOverride: string | null | undefined;
  contactTitle: string;
  company: string;
  firm: string;
  email: string;
  phone: string;
  activities: Activity[];
  engagementText: string;
  relationshipState: { relationshipState?: string } | null;
  doNotBulkEnrichLocal: boolean;
  setDoNotBulkEnrichLocal: (b: boolean) => void;
  avatarColor: { bg: string; text: string };
  getInitials: (fn: string, ln: string) => string;
  onSave: (data: Partial<ContactObject>) => void;
  // Edit/Done toggle setters
  setName: (s: string) => void;
  summary: string;
  linkedinUrl: string;
  location: string;
  enrichmentSummary: string;
  enrichmentSkills: string[];
  enrichmentConfidence: string;
  enrichmentAttemptedAt: string;
  enrichHints: string;
  setEnrichmentSummary: (s: string) => void;
  setEnrichmentSkills: (s: string[]) => void;
  setEnrichmentConfidence: (s: string) => void;
  setEnrichmentAttemptedAt: (s: string) => void;
  setContactTitle: (s: string) => void;
  setLocation: (s: string) => void;
  setLinkedinUrl: (s: string) => void;
  setSummary: (s: string) => void;
  setFirm: (s: string) => void;
  markDirty: () => void;
  scrollBody: React.ReactNode;
}

export function ContactEditHeaderBar(props: ContactEditHeaderBarProps) {
  const {
    isMobile, viewOnly, isNew, isSaving, freeformMode, isViewMode, setIsViewMode, onClose,
    contact, columns, stage, showStageDropdown, setShowStageDropdown, stageDropdownRef,
    handleStageChange, showHeaderMenu, setShowHeaderMenu, headerMenuRef, onArchive, onDelete,
    showArchiveConfirm, setShowArchiveConfirm, setDeleteConfirmation, enrichLoading, handleEnrich,
    name, firstName, lastName, displayNameOverride, contactTitle, company, firm, email, phone,
    activities, engagementText, relationshipState, doNotBulkEnrichLocal, setDoNotBulkEnrichLocal,
    avatarColor, getInitials, onSave, setName, summary, linkedinUrl, location, enrichmentSummary,
    enrichmentSkills, enrichmentConfidence, enrichmentAttemptedAt, enrichHints,
    setEnrichmentSummary, setEnrichmentSkills, setEnrichmentConfidence, setEnrichmentAttemptedAt,
    setContactTitle, setLocation, setLinkedinUrl, setSummary, setFirm, markDirty, scrollBody,
  } = props;

  return (
    <>
      {/* Header bar */}
      <div style={{
        flexShrink: 0,
        padding: isMobile ? '0 16px' : '0 20px',
        borderBottom: '1px solid #DADCE0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: isMobile ? 44 : 48,
        boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8, border: 'none',
                background: 'none', cursor: 'pointer', color: 'var(--color-brand)',
                marginLeft: -8, flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
          )}
          {isMobile && !freeformMode ? (
            <span
              onClick={onClose}
              style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-brand)', cursor: 'pointer' }}
            >Back</span>
          ) : (
            <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>
              {freeformMode ? 'New Contact' : formatPersonName(name || `${firstName} ${lastName}`.trim() || 'Untitled', displayNameOverride)}
            </span>
          )}
          {/* Header "draws from" provenance badges (§3.5) — visible in both
              view and edit modes; renders nothing for native contacts. */}
          {!freeformMode && <ContactProvenanceBadges contact={contact} />}
          {/* Read-only scalar conflict indicator (§3.6) — surfaces fields whose
              value diverges across provider accounts; renders nothing when the
              contact carries no conflicted fieldProvenance (native/legacy). */}
          {!freeformMode && <ContactConflictBadges contact={contact} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Lookup/AI button — hide in freeform mode; on mobile, shown below header */}
          {!isMobile && !viewOnly && !freeformMode && firstName.trim() && (
            <button
              type="button"
              className="contact-ai-enhance-btn"
              onClick={() => handleEnrich()}
              disabled={enrichLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', fontSize: 13, fontWeight: 500,
                color: enrichLoading ? '#7a7f85' : '#4a4d51',
                background: '#F1F3F4',
                border: 'none', borderRadius: 6, cursor: enrichLoading ? 'default' : 'pointer',
                transition: 'all 0.15s',
                opacity: enrichLoading ? 0.7 : 1,
              }}
            >
              {enrichLoading ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" opacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
              {enrichLoading ? 'Enriching...' : 'AI Enhance'}
            </button>
          )}
          {/* Edit / Done toggle — on mobile, shown below header */}
          {!isMobile && !viewOnly && !isNew && (
            <button
              type="button"
              onClick={() => {
                if (!isViewMode) {
                  // Switching from edit to view — save without closing modal
                  const trimmedFirst = firstName.trim();
                  const trimmedLast = lastName.trim();
                  const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
                  if (fullName) {
                    // Check if name or company changed — if so, invalidate enrichment
                    const originalName = contact?.name || contact?.title || '';
                    const originalOrg = contact?.organization || contact?.company || (contact?.attributes as any)?.organization || '';
                    const nameChanged = fullName !== originalName;
                    const orgChanged = company.trim() !== originalOrg;
                    const shouldInvalidateEnrichment = nameChanged || orgChanged;

                    const saveData: Record<string, any> = {
                      name: fullName,
                      emails: email.trim() ? [email.trim()] : [],
                      phones: phone.trim() ? [phone.trim()] : [],
                      organization: company.trim(),
                      notes: activitiesToNotes(activities),
                      activities,
                    };

                    if (shouldInvalidateEnrichment) {
                      // Clear enrichment — name or company changed so it's stale
                      saveData.enrichmentSummary = '';
                      saveData.enrichmentSkills = [];
                      saveData.enrichmentConfidence = '';
                      saveData.enrichmentAttemptedAt = '';
                      saveData.contactTitle = '';
                      saveData.location = '';
                      saveData.linkedinUrl = '';
                      saveData.firm = company.trim();
                      saveData.summary = '';
                      // Clear local state
                      setEnrichmentSummary('');
                      setEnrichmentSkills([]);
                      setEnrichmentConfidence('');
                      setEnrichmentAttemptedAt('');
                      setContactTitle('');
                      setLocation('');
                      setLinkedinUrl('');
                      setSummary('');
                      setFirm(company.trim());
                    } else {
                      if (contactTitle.trim()) saveData.contactTitle = contactTitle.trim();
                      if (linkedinUrl.trim()) saveData.linkedinUrl = linkedinUrl.trim();
                      if (firm.trim()) saveData.firm = firm.trim();
                      if (location.trim()) saveData.location = location.trim();
                      if (summary.trim()) saveData.summary = summary.trim();
                      if (enrichmentSummary) saveData.enrichmentSummary = enrichmentSummary;
                      if (enrichmentSkills.length) saveData.enrichmentSkills = enrichmentSkills;
                      if (enrichmentConfidence) saveData.enrichmentConfidence = enrichmentConfidence;
                      if (enrichmentAttemptedAt) saveData.enrichmentAttemptedAt = enrichmentAttemptedAt;
                    }
                    if (enrichHints.trim()) saveData.enrichHints = enrichHints.trim();
                    // Use a special flag so parent doesn't close the modal
                    saveData._stayOpen = true;
                    onSave(saveData as any);
                    // Update local name state for view mode
                    setName(fullName);
                  }
                }
                setIsViewMode(!isViewMode);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', fontSize: 13, fontWeight: 500,
                color: isViewMode ? 'var(--color-brand)' : '#4a4d51',
                background: isViewMode ? 'var(--xavior-blue-bg, #e0f2fe)' : '#F1F3F4',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {isViewMode ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </>
              ) : (
                'Done'
              )}
            </button>
          )}
          {/* Save lives next to Cancel at the bottom of the form
              on desktop — duplicating it in the header was creating
              two visible Save buttons. Mobile keeps its inline
              header Save below since that layout has no bottom bar. */}
          {/* Stage token dropdown — desktop hidden per product
              direction; stage changes happen via Kanban drag.
              Mobile keeps it for now (Kanban drag is awkward on
              touch); revisit when we redo mobile. */}
          {isMobile && columns.length > 0 && !isNew && (() => {
            const currentColumn = columns.find(col => col.stage === stage);
            const stageColor = '#f1f5f9';
            return (
            <div ref={stageDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowStageDropdown(!showStageDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#4a4d51',
                  background: stageColor,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                  transform: showStageDropdown ? 'translateY(-1px)' : 'translateY(0)',
                  boxShadow: showStageDropdown ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                  minWidth: 110,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  if (!showStageDropdown) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                  }
                }}
              >
                {currentColumn?.label || 'Lead'}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, transform: showStageDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {showStageDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 4,
                  background: 'var(--color-surface)',
                  border: '1px solid #DADCE0',
                  borderRadius: 8,
                  boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0, 0, 0, 0.15))',
                  minWidth: 110,
                  zIndex: 10002,
                  overflow: 'hidden',
                }}>
                  {columns
                    .filter(col => col.stage !== stage)
                    .map(col => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => handleStageChange(col.stage)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 400,
                        color: 'var(--color-text)',
                        background: 'var(--color-surface)',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'font-weight 0.15s',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.fontWeight = '600';
                        e.currentTarget.style.filter = 'brightness(0.95)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.fontWeight = '400';
                        e.currentTarget.style.filter = 'brightness(1)';
                      }}
                    >
                      {col.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            );
          })()}
          {/* Overflow menu (archive/delete) */}
          {!isNew && contact?.id && (
            <div ref={headerMenuRef} style={{ position: 'relative' }}>
              <button type="button" onClick={() => setShowHeaderMenu(!showHeaderMenu)} style={{
                width: 44, height: 44, borderRadius: 8, border: 'none',
                background: 'none', cursor: 'pointer', fontSize: 19, fontWeight: 700, color: 'var(--color-text-muted)', lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: -8,
              }}>
                ···
              </button>
              {showHeaderMenu && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8,
                  boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0,0,0,0.12))', minWidth: 160, zIndex: 10002,
                  overflow: 'hidden',
                }}>
                  <HeaderOverflowMenuItems
                    showArchive={Boolean(onArchive)}
                    showDelete={Boolean(onDelete && contact?.id)}
                    onArchive={() => { setShowHeaderMenu(false); setShowArchiveConfirm(true); }}
                    onDelete={() => { setShowHeaderMenu(false); if (contact?.id) setDeleteConfirmation({ contactId: contact.id, name: name || `${firstName} ${lastName}`.trim() }); }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Archive confirmation modal */}
      {contact?.id && (
        <ArchiveContactConfirmationModal
          show={showArchiveConfirm}
          displayName={name || `${firstName} ${lastName}`.trim()}
          onCancel={() => setShowArchiveConfirm(false)}
          onArchive={() => { setShowArchiveConfirm(false); if (onArchive) { onArchive(contact.id); onClose(); } }}
        />
      )}

      {/* Left column scrollable content */}
      <div className="xv-scroll" style={{ overflowY: 'auto', flex: 1, minHeight: 0, WebkitOverflowScrolling: 'touch' }} onInput={markDirty} onChange={markDirty}>

      {/* ---- Identity Block (always visible) ---- */}
      <div style={{ padding: '20px 20px 0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar */}
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: avatarColor.bg, color: avatarColor.text,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 17, fontWeight: 700, flexShrink: 0,
          }}>
            {getInitials(firstName, lastName)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.2 }}>
              {name || `${firstName} ${lastName}`.trim() || 'Untitled'}
            </div>
            {(contactTitle || company || firm) && (
              <div style={{ fontSize: 13, color: '#4a4d51', marginTop: 2 }}>
                {[contactTitle, firm || company].filter(Boolean).join(' · ')}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              {/* Stage chip — hidden on mobile (shown in action bar) */}
              {!isMobile && columns.length > 0 && !isNew && (() => {
                const currentColumn = columns.find(col => col.stage === stage);
                return currentColumn ? (
                  <span style={{
                    fontSize: 12, fontWeight: 600, padding: '2px 8px',
                    borderRadius: 12, background: 'var(--xavior-blue-bg, #e0f2fe)', color: 'var(--color-brand)',
                  }}>{currentColumn.label}</span>
                ) : null;
              })()}
              {/* Engagement chip — hidden on mobile (redundant with action bar) */}
              {!isMobile && !isNew && (
                <span style={{
                  fontSize: 12, fontWeight: 500, padding: '2px 8px',
                  borderRadius: 12, background: '#F1F3F4', color: '#4a4d51',
                }}>{engagementText}</span>
              )}
              {/* Relationship status badge */}
              {!isNew && relationshipState && (() => {
                const rs = relationshipState.relationshipState;
                const badge = rs === 'awaiting_your_reply'
                  ? { label: 'Needs Reply', bg: '#FEE2E2', color: '#991B1B', border: '#FCA5A5' }
                  : rs === 'awaiting_their_reply'
                  ? { label: 'Awaiting Response', bg: '#DCFCE7', color: '#166534', border: '#86EFAC' }
                  : rs === 'stalled'
                  ? { label: 'Follow-up Recommended', bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }
                  : rs === 'active'
                  ? { label: 'Active', bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' }
                  : null;
                if (!badge) return null;
                return (
                  <span style={{
                    fontSize: 12, fontWeight: 600, padding: '2px 8px',
                    borderRadius: 12, background: badge.bg, color: badge.color,
                    border: `1px solid ${badge.border}`,
                  }}>{badge.label}</span>
                );
              })()}
              {/* Auto-enrich toggle badge (#2186). Always visible so the
                  state is at-a-glance and either direction is one click.
                  ON = subtle gray; OFF = amber to draw attention.
                  Uses optimistic local state so the toggle flips
                  instantly — the parent's _stayOpen save path doesn't
                  refetch the contact otherwise. */}
              {!isNew && contact && (
                <button
                  type="button"
                  onClick={() => {
                    const next = !doNotBulkEnrichLocal;
                    setDoNotBulkEnrichLocal(next);
                    onSave({
                      doNotBulkEnrich: next,
                      _stayOpen: true,
                    } as Partial<ContactObject> & { _stayOpen?: boolean });
                  }}
                  title={doNotBulkEnrichLocal
                    ? 'Auto-enrichment is OFF. Click to re-enable.'
                    : 'Auto-enrichment is ON. Click to opt out — this contact won\'t be re-enriched automatically.'}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '2px 8px',
                    borderRadius: 12,
                    background: doNotBulkEnrichLocal ? '#FEF3C7' : 'transparent',
                    color: doNotBulkEnrichLocal ? '#92400E' : '#9aa0a6',
                    border: doNotBulkEnrichLocal ? '1px solid #FCD34D' : '1px solid #DADCE0',
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {doNotBulkEnrichLocal ? '⏸ Auto-enrich off' : 'Auto-enrich on'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile action bar — AI, Edit, Stage, Save — below contact info */}
      {isMobile && !freeformMode && !isNew && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          borderBottom: '1px solid #DADCE0', flexWrap: 'wrap',
        }}>
          {/* AI enrich button */}
          {!viewOnly && firstName.trim() && (
            <button
              type="button"
              className="contact-ai-enhance-btn"
              onClick={() => handleEnrich()}
              disabled={enrichLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', fontSize: 13, fontWeight: 500,
                color: enrichLoading ? '#7a7f85' : '#4a4d51',
                background: '#F1F3F4',
                border: 'none', borderRadius: 6, cursor: enrichLoading ? 'default' : 'pointer',
                transition: 'all 0.15s',
                opacity: enrichLoading ? 0.7 : 1,
              }}
            >
              {enrichLoading ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="12" cy="12" r="10" opacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
              {enrichLoading ? 'Enriching...' : 'AI Enhance'}
            </button>
          )}
          {/* Edit / Done toggle */}
          {!viewOnly && (
            <button
              type="button"
              onClick={() => {
                if (!isViewMode) {
                  const trimmedFirst = firstName.trim();
                  const trimmedLast = lastName.trim();
                  const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
                  if (fullName) {
                    const originalName = contact?.name || contact?.title || '';
                    const originalOrg = contact?.organization || contact?.company || (contact?.attributes as any)?.organization || '';
                    const nameChanged = fullName !== originalName;
                    const orgChanged = company.trim() !== originalOrg;
                    const shouldInvalidateEnrichment = nameChanged || orgChanged;
                    const saveData: Record<string, any> = {
                      name: fullName,
                      emails: email.trim() ? [email.trim()] : [],
                      phones: phone.trim() ? [phone.trim()] : [],
                      organization: company.trim(),
                      notes: activitiesToNotes(activities),
                      activities,
                    };
                    if (shouldInvalidateEnrichment) {
                      saveData.enrichmentSummary = '';
                      saveData.enrichmentSkills = [];
                      saveData.enrichmentConfidence = '';
                      saveData.enrichmentAttemptedAt = '';
                      saveData.contactTitle = '';
                      saveData.location = '';
                      saveData.linkedinUrl = '';
                      saveData.firm = company.trim();
                      saveData.summary = '';
                      setEnrichmentSummary('');
                      setEnrichmentSkills([]);
                      setEnrichmentConfidence('');
                      setEnrichmentAttemptedAt('');
                      setContactTitle('');
                      setLocation('');
                      setLinkedinUrl('');
                      setSummary('');
                      setFirm(company.trim());
                    } else {
                      if (contactTitle.trim()) saveData.contactTitle = contactTitle.trim();
                      if (linkedinUrl.trim()) saveData.linkedinUrl = linkedinUrl.trim();
                      if (firm.trim()) saveData.firm = firm.trim();
                      if (location.trim()) saveData.location = location.trim();
                      if (summary.trim()) saveData.summary = summary.trim();
                      if (enrichmentSummary) saveData.enrichmentSummary = enrichmentSummary;
                      if (enrichmentSkills.length) saveData.enrichmentSkills = enrichmentSkills;
                      if (enrichmentConfidence) saveData.enrichmentConfidence = enrichmentConfidence;
                      if (enrichmentAttemptedAt) saveData.enrichmentAttemptedAt = enrichmentAttemptedAt;
                    }
                    if (enrichHints.trim()) saveData.enrichHints = enrichHints.trim();
                    saveData._stayOpen = true;
                    onSave(saveData as any);
                    setName(fullName);
                  }
                }
                setIsViewMode(!isViewMode);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', fontSize: 13, fontWeight: 500,
                color: isViewMode ? 'var(--color-brand)' : '#4a4d51',
                background: isViewMode ? 'var(--xavior-blue-bg, #e0f2fe)' : '#F1F3F4',
                border: 'none', borderRadius: 6, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {isViewMode ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </>
              ) : (
                'Done'
              )}
            </button>
          )}
          {/* Save button */}
          {!viewOnly && !isViewMode && (
            <button
              type="button"
              className="contact-save-btn"
              disabled={isSaving}
              onClick={() => {
                const form = document.querySelector<HTMLFormElement>('.contact-edit-form');
                if (form) form.requestSubmit();
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 14px', fontSize: 13, fontWeight: 600,
                background: 'var(--color-brand)', color: 'white', border: 'none',
                borderRadius: 6, cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.6 : 1, transition: 'all 0.15s',
              }}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          )}
          {/* Stage dropdown */}
          {columns.length > 0 && (() => {
            const currentColumn = columns.find(col => col.stage === stage);
            const stageColor = '#f1f5f9';
            return (
              <div ref={stageDropdownRef} style={{ position: 'relative', marginLeft: 'auto' }}>
                <button
                  type="button"
                  onClick={() => setShowStageDropdown(!showStageDropdown)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '6px 12px', fontSize: 12, fontWeight: 500,
                    color: '#4a4d51', background: stageColor,
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                    whiteSpace: 'nowrap', transition: 'all 0.15s',
                    minWidth: 110,
                    boxShadow: showStageDropdown ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  {currentColumn?.label || 'Lead'}
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, transform: showStageDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>
                {showStageDropdown && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8,
                    boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0, 0, 0, 0.15))', minWidth: 110, zIndex: 10002,
                    overflow: 'hidden',
                  }}>
                    {columns
                      .filter(col => col.stage !== stage)
                      .map(col => (
                      <button
                        key={col.id}
                        type="button"
                        onClick={() => handleStageChange(col.stage)}
                        style={{
                          display: 'flex', alignItems: 'center', width: '100%',
                          padding: '8px 12px', fontSize: 12, fontWeight: 400,
                          color: 'var(--color-text)', background: 'var(--color-surface)', border: 'none',
                          cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
                        }}
                      >
                        {col.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {scrollBody}
      </div>
    </>
  );
}
